import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Sparkles, Gift, Check, Banknote, CreditCard } from "lucide-react";

export default function Booking() {
  const { user, refresh } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [packages, setPackages] = useState([]);
  const [settings, setSettings] = useState(null);
  const [packageId, setPackageId] = useState(location.state?.packageId || "");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [availability, setAvailability] = useState(null);
  const [address, setAddress] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [vehicleCategory, setVehicleCategory] = useState("mittelklasse");
  const [notes, setNotes] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [useFreeCredit, setUseFreeCredit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successBooking, setSuccessBooking] = useState(null);
  // Guest fields
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");

  useEffect(() => {
    api.get("/packages").then((r) => {
      setPackages(r.data);
      if (!packageId && r.data[0]) setPackageId(r.data[0].package_id);
    });
    api.get("/settings").then((r) => setSettings(r.data));
  }, []); // eslint-disable-line

  const selected = packages.find((p) => p.package_id === packageId);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        package_id: packageId,
        date,
        time,
        address,
        vehicle,
        notes,
        payment_method: paymentMethod,
        use_free_credit: useFreeCredit,
      };
      if (!user) {
        payload.guest_name = guestName;
        payload.guest_email = guestEmail;
        payload.guest_phone = guestPhone;
      }
      const { data } = await api.post("/bookings", payload);
      if (user) await refresh();
      setSuccessBooking(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler bei der Buchung");
    } finally {
      setSubmitting(false);
    }
  };

  if (!packages.length) return null;

  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="min-h-screen grain">
      <Header />
      <main className="relative max-w-5xl mx-auto px-6 sm:px-10 py-12">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">Buchung</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight">
            Termin <span className="text-gradient-gold">vereinbaren</span>
          </h1>
        </div>

        <form onSubmit={onSubmit} className="grid lg:grid-cols-[1fr_360px] gap-8">
          <div className="space-y-8">
            {/* Guest contact (only when not logged in) */}
            {!user && (
              <Section title="1. Deine Kontaktdaten">
                <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 mb-4 text-xs text-zinc-300">
                  Du buchst als Gast — kein Konto nötig. Wenn du{" "}
                  <button
                    type="button"
                    onClick={() => navigate("/register")}
                    className="text-primary hover:underline"
                    data-testid="guest-register-hint"
                  >
                    ein Konto erstellst
                  </button>
                  , sammelst du automatisch Stempel für deine Gratis-Aufbereitung.
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Name</Label>
                    <Input
                      data-testid="guest-name"
                      required
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      className="mt-2 bg-zinc-900 border-zinc-800 h-11"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">E-Mail</Label>
                    <Input
                      data-testid="guest-email"
                      type="email"
                      required
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      className="mt-2 bg-zinc-900 border-zinc-800 h-11"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Telefon</Label>
                    <Input
                      data-testid="guest-phone"
                      required
                      placeholder="z. B. 0151 …"
                      value={guestPhone}
                      onChange={(e) => setGuestPhone(e.target.value)}
                      className="mt-2 bg-zinc-900 border-zinc-800 h-11"
                    />
                  </div>
                </div>
              </Section>
            )}

            {/* Package */}
            <Section title={`${user ? "1" : "2"}. Paket wählen`}>
              <div className="grid sm:grid-cols-2 gap-4">
                {packages.map((p) => (
                  <button
                    key={p.package_id}
                    type="button"
                    data-testid={`booking-package-${p.package_id}`}
                    onClick={() => setPackageId(p.package_id)}
                    className={`text-left rounded-xl p-5 border transition-all ${
                      packageId === p.package_id
                        ? "border-primary bg-primary/5"
                        : "border-white/10 bg-zinc-950 hover:border-white/30"
                    }`}
                  >
                    <div className="flex items-baseline justify-between">
                      <div className="font-heading text-lg">{p.name}</div>
                      <div className="text-gradient-gold font-heading text-xl">{p.price.toFixed(0)}€</div>
                    </div>
                    <div className="text-xs text-zinc-400 mt-2 leading-relaxed">{p.description}</div>
                  </button>
                ))}
              </div>
            </Section>

            {/* Schedule */}
            <Section title={`${user ? "2" : "3"}. Datum & Uhrzeit`}>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Datum</Label>
                  <Input
                    data-testid="booking-date"
                    type="date"
                    required
                    min={today}
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="mt-2 bg-zinc-900 border-zinc-800 h-11"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Uhrzeit</Label>
                  {!date ? (
                    <div className="mt-2 text-xs text-zinc-500 h-11 flex items-center">
                      Bitte zuerst ein Datum wählen
                    </div>
                  ) : availability?.closed ? (
                    <div data-testid="slot-day-closed" className="mt-2 text-xs text-red-400 h-11 flex items-center">
                      {availability?.reason || "Geschlossen"} — bitte anderes Datum wählen
                    </div>
                  ) : availability?.full ? (
                    <div data-testid="slot-day-full" className="mt-2 text-xs text-red-400 h-11 flex items-center">
                      Dieser Tag ist leider ausgebucht
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {(availability?.slots || []).map((s) => {
                        const isBooked = availability?.booked?.includes(s);
                        const isAvailable = availability?.available?.includes(s);
                        const isSelected = time === s;
                        return (
                          <button
                            key={s}
                            type="button"
                            data-testid={`slot-${s}`}
                            disabled={!isAvailable}
                            onClick={() => setTime(s)}
                            className={
                              isSelected
                                ? "rounded-lg border border-primary bg-primary/15 text-primary py-2.5 text-sm font-medium"
                                : isBooked
                                ? "rounded-lg border border-white/5 bg-zinc-900/40 text-zinc-600 py-2.5 text-sm line-through cursor-not-allowed"
                                : "rounded-lg border border-white/10 bg-zinc-900 text-zinc-200 py-2.5 text-sm hover:border-white/30"
                            }
                          >
                            {s}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <div className="text-[10px] text-zinc-500 mt-2">
                    Mo–Sa · 90 Min. Aufbereitung + 30 Min. Fahrzeit-Puffer · max. 5 Termine pro Tag · Feiertage geschlossen
                  </div>
                  <input type="hidden" name="time" value={time} required />
                </div>
              </div>
            </Section>

            {/* Location & vehicle */}
            <Section title={`${user ? "3" : "4"}. Adresse & Fahrzeug`}>
              <div className="grid gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Adresse (ich komme zu dir)</Label>
                  <Input
                    data-testid="booking-address"
                    required
                    placeholder="Straße, PLZ, Stadt"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    className="mt-2 bg-zinc-900 border-zinc-800 h-11"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Fahrzeug</Label>
                  <Input
                    data-testid="booking-vehicle"
                    required
                    placeholder="z. B. BMW 3er, Schwarz, Kennzeichen"
                    value={vehicle}
                    onChange={(e) => setVehicle(e.target.value)}
                    className="mt-2 bg-zinc-900 border-zinc-800 h-11"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Fahrzeugkategorie</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                    {[
                      { v: "kleinwagen", l: "Kleinwagen", h: "z. B. Polo, Corsa" },
                      { v: "mittelklasse", l: "Mittelklasse", h: "z. B. Golf, Astra" },
                      { v: "suv", l: "SUV", h: "z. B. Tiguan, X3" },
                      { v: "van", l: "Van", h: "z. B. Sharan, V-Klasse" },
                    ].map((c) => (
                      <button
                        key={c.v}
                        type="button"
                        data-testid={`vehicle-cat-${c.v}`}
                        onClick={() => setVehicleCategory(c.v)}
                        className={`text-left rounded-xl border px-3 py-2.5 transition-all ${
                          vehicleCategory === c.v
                            ? "border-primary bg-primary/10"
                            : "border-white/10 bg-zinc-900 hover:border-white/30"
                        }`}
                      >
                        <div className="text-sm font-medium">{c.l}</div>
                        <div className="text-[10px] text-zinc-500">{c.h}</div>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Anmerkungen (optional)</Label>
                  <Textarea
                    data-testid="booking-notes"
                    rows={3}
                    placeholder="Besondere Wünsche, Tierhaare, hartnäckige Flecken …"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="mt-2 bg-zinc-900 border-zinc-800"
                  />
                </div>
              </div>
            </Section>

            {/* Payment */}
            <Section title="4. Zahlung">
              <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="grid sm:grid-cols-2 gap-3">
                <PayOption value="cash" current={paymentMethod} icon={<Banknote className="w-5 h-5" />} title="Vor Ort" desc="Bar bei Abschluss" />
                <PayOption value="transfer" current={paymentMethod} icon={<CreditCard className="w-5 h-5" />} title="Überweisung" desc="Per IBAN nach Termin" />
              </RadioGroup>

              {paymentMethod === "transfer" && settings && (
                <div className="mt-4 rounded-xl border border-primary/20 bg-primary/5 p-4 text-sm">
                  <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Bankverbindung</div>
                  <div className="grid sm:grid-cols-2 gap-2 text-zinc-300">
                    <div>Empfänger: <span className="text-white">{settings.account_holder}</span></div>
                    <div>Bank: <span className="text-white">{settings.bank_name}</span></div>
                    <div className="sm:col-span-2">IBAN: <span className="text-white font-mono">{settings.iban}</span></div>
                    {settings.bic && <div>BIC: <span className="text-white font-mono">{settings.bic}</span></div>}
                  </div>
                </div>
              )}
            </Section>

            {/* Free credit */}
            {user && (user.free_credits || 0) > 0 && (
              <div className="rounded-xl border border-primary/40 bg-primary/10 p-5 flex items-start gap-3">
                <Checkbox
                  data-testid="booking-use-free-credit"
                  id="freecredit"
                  checked={useFreeCredit}
                  onCheckedChange={(v) => setUseFreeCredit(!!v)}
                />
                <label htmlFor="freecredit" className="text-sm cursor-pointer">
                  <div className="flex items-center gap-2 font-medium text-primary">
                    <Gift className="w-4 h-4" /> Gratis-Aufbereitung einlösen
                  </div>
                  <div className="text-xs text-zinc-300 mt-1">
                    Du hast {user.free_credits}× Gratis verfügbar — beim Einlösen wird der Preis 0,00€.
                  </div>
                </label>
              </div>
            )}
          </div>

          {/* Summary */}
          <aside className="lg:sticky lg:top-24 h-fit">
            <div className="rounded-2xl border border-white/10 bg-zinc-950 p-6">
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">Zusammenfassung</div>
              {selected ? (
                <>
                  <div className="font-heading text-xl mb-2">{selected.name}</div>
                  <div className="text-xs text-zinc-400 leading-relaxed mb-6">{selected.description}</div>
                  <div className="border-t border-white/10 pt-4 flex items-baseline justify-between">
                    <div className="text-sm text-zinc-400">Gesamt</div>
                    <div className="font-heading text-3xl text-gradient-gold">
                      {useFreeCredit ? "0,00€" : `${selected.price.toFixed(2)}€`}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-zinc-500 text-sm">Paket auswählen</div>
              )}
              <Button
                data-testid="booking-submit"
                type="submit"
                disabled={submitting || !packageId || !time || !date}
                className="w-full mt-6 rounded-full bg-primary text-primary-foreground hover:brightness-110 h-11"
              >
                {submitting ? "Wird gebucht…" : "Termin buchen"}
              </Button>
              <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 mt-6 text-xs text-amber-200/90">
                <strong className="text-amber-300">Hinweis zum Preis:</strong> Der angezeigte Preis ist ein Richtpreis. Bei stärkerer Verschmutzung
                (z. B. Tierhaare, hartnäckige Flecken, starke Verschmutzung durch Kinder, Baustellenreste o. Ä.)
                kann sich der Preis je nach Zeitaufwand erhöhen. Du wirst vor der Aufbereitung
                über mögliche Mehrkosten informiert.
              </div>
              <div className="text-[10px] text-zinc-500 mt-3 text-center">
                {user
                  ? "Mit der Buchung sammelst du automatisch einen Stempel."
                  : "Tipp: Mit Konto sammelst du Stempel für deine Gratis-Aufbereitung."}
              </div>
            </div>
          </aside>
        </form>
      </main>

      <Dialog open={!!successBooking} onOpenChange={(o) => !o && (setSuccessBooking(null), navigate(user ? "/dashboard" : "/"))}>
        <DialogContent className="bg-zinc-950 border-white/10">
          <DialogHeader>
            <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-2">
              <Check className="w-6 h-6 text-primary" strokeWidth={3} />
            </div>
            <DialogTitle className="font-heading text-2xl text-center">Buchung bestätigt</DialogTitle>
            <DialogDescription className="text-center text-zinc-400">
              Wir bestätigen deinen Termin in Kürze. Du findest alle Details in deinem Konto.
            </DialogDescription>
          </DialogHeader>
          {successBooking && (
            <div className="text-sm text-zinc-300 space-y-1 border-t border-white/10 pt-4">
              <div>📅 {successBooking.date} · {successBooking.time}</div>
              <div>🚗 {successBooking.vehicle}</div>
              <div>📍 {successBooking.address}</div>
              <div>💳 {successBooking.payment_method === "cash" ? "Barzahlung vor Ort" : "Überweisung"}</div>
            </div>
          )}
          <Button
            data-testid="booking-success-close"
            onClick={() => navigate(user ? "/dashboard" : "/")}
            className="rounded-full bg-primary text-primary-foreground hover:brightness-110"
          >
            {user ? "Zum Konto" : "Zur Startseite"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">{title}</div>
      {children}
    </div>
  );
}

function PayOption({ value, current, icon, title, desc }) {
  const selected = current === value;
  return (
    <label
      data-testid={`booking-payment-${value}`}
      className={`flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-all ${
        selected ? "border-primary bg-primary/5" : "border-white/10 bg-zinc-950 hover:border-white/30"
      }`}
    >
      <RadioGroupItem value={value} className="mt-1" />
      <div>
        <div className="flex items-center gap-2 font-medium">{icon} {title}</div>
        <div className="text-xs text-zinc-400 mt-1">{desc}</div>
      </div>
    </label>
  );
}
